import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { contentAPI } from '../../services/api';
import SelectInput from '../../components/form/SelectInput';
import TextInput from '../../components/form/TextInput';
import TextArea from '../../components/form/TextArea';
import PhoneInput from '../../components/form/PhoneInput';
import EmailInput from '../../components/form/EmailInput';
import NumberInput from '../../components/form/NumberInput';
import DateInput from '../../components/form/DateInput';
import CheckboxInput from '../../components/form/CheckboxInput';
import '../../components/form/FormStyles.css';
import './AdminStaffMemberForm.css';

const categoryOptions = [
  { value: 'administrative', label: 'Administrative (प्रशासकीय)' },
  { value: 'technical', label: 'Technical (तांत्रिक)' },
  { value: 'support', label: 'Support (सहायक)' },
  { value: 'contracted', label: 'Contracted (करार आधारित)' },
  { value: 'elected_member', label: 'Elected Member (निवडून आलेला सदस्य)' },
  { value: 'other', label: 'Other (इतर)' },
];

const availabilityOptions = [
  { value: 'available', label: 'Available (उपलब्ध)' },
  { value: 'on_leave', label: 'On Leave (सुट्टीवर)' },
  { value: 'retired', label: 'Retired (निवृत्त)' },
  { value: 'transferred', label: 'Transferred (स्थानांतरित)' },
];

const genderOptions = [
  { value: 'Male', label: 'Male (पुरुष)' },
  { value: 'Female', label: 'Female (महिला)' },
  { value: 'Other', label: 'Other (इतर)' },
];

const nullableFields = ['department_id', 'experience_years', 'date_of_birth', 'gender', 'appointment_date'];
const integerFields = ['department_id', 'experience_years'];

const apiErrorMessage = (error, fallback) => {
  const details = error.response?.data?.details;
  if (Array.isArray(details) && details.length) {
    return details.map(item => `${item.field || 'field'}: ${item.message}`).join(', ');
  }
  return error.response?.data?.error || fallback;
};

const normalizeStaffPayload = (data) => {
  const payload = { ...data };
  nullableFields.forEach((field) => {
    if (payload[field] === '') payload[field] = null;
  });
  integerFields.forEach((field) => {
    if (payload[field] !== null && payload[field] !== undefined) {
      const parsed = Number(payload[field]);
      payload[field] = Number.isFinite(parsed) ? parsed : null;
    }
  });
  return payload;
};

export default function AdminStaffMemberForm() {
  const { tenantSlug, staffId } = useParams();
  const navigate = useNavigate();
  const [departments, setDepartments] = useState([]);
  const [formData, setFormData] = useState({
    full_name: '',
    designation: '',
    designation_marathi: '',
    phone: '',
    email: '',
    department_id: '',
    office_phone: '',
    qualification: '',
    experience_years: '',
    address: '',
    photo_url: '',
    date_of_birth: '',
    gender: '',
    category: 'administrative',
    appointment_date: '',
    office_location: '',
    specialization: '',
    certifications: '',
    availability: 'available',
    public_visible: true,
    is_active: true,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(staffId ? true : false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);
  const steps = [
    { title: 'वैयक्तिक माहिती', subtitle: 'Personal details' },
    { title: 'पद व विभाग', subtitle: 'Designation' },
    { title: 'संपर्क माहिती', subtitle: 'Contact information' },
    { title: 'व्यावसायिक माहिती', subtitle: 'Employment' },
    { title: 'पुनरावलोकन', subtitle: 'Visibility & review' },
  ];

  // Fetch departments and existing staff member
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          deptsRes,
          staffRes,
        ] = await Promise.all([
          api.get(`/tenant/${tenantSlug}/departments/admin/list`),
          staffId ? api.get(`/tenant/${tenantSlug}/staff/admin/${staffId}`) : Promise.resolve(null),
        ]);

        const deptOptions = deptsRes.data.map((d) => ({
          value: d.id,
          label: d.name,
        }));
        setDepartments(deptOptions);

        if (staffRes?.data) {
          const loadedPhone = String(staffRes.data.phone || '').replace(/\D/g, '');
          setFormData({
            ...staffRes.data,
            phone: loadedPhone,
            public_visible: staffRes.data.public_visible !== false,
            is_active: staffRes.data.is_active !== false,
          });
        }
      } catch (error) {
        setErrors({ submit: 'Failed to load data' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [staffId, tenantSlug]);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = 'Full name is required';
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';
    if (!formData.phone || formData.phone.replace(/\D/g, '').length !== 10) {
      newErrors.phone = 'Valid 10-digit phone is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (formData.experience_years && (isNaN(formData.experience_years) || formData.experience_years < 0)) {
      newErrors.experience_years = 'Experience must be a valid number';
    }
    if (formData.date_of_birth) {
      const age = calculateAge(formData.date_of_birth);
      if (age < 18) {
        newErrors.date_of_birth = 'Must be at least 18 years old';
      }
    }
    if (formData.appointment_date) {
      const appointDate = new Date(formData.appointment_date);
      if (appointDate > new Date()) {
        newErrors.appointment_date = 'Appointment date cannot be in the future';
      }
    }
    return newErrors;
  };

  const stepFields = [
    ['full_name', 'date_of_birth'],
    ['designation'],
    ['phone', 'email'],
    ['experience_years', 'appointment_date'],
    [],
  ];

  const validateStep = (stepIndex) => {
    const allErrors = validateForm();
    const stepErrors = {};
    stepFields[stepIndex].forEach(field => {
      if (allErrors[field]) stepErrors[field] = allErrors[field];
    });
    return stepErrors;
  };

  const moveStep = (nextStep) => {
    if (nextStep > currentStep) {
      const stepErrors = validateStep(currentStep);
      if (Object.keys(stepErrors).length) {
        setErrors(current => ({ ...current, ...stepErrors }));
        return;
      }
    }
    setCurrentStep(Math.max(0, Math.min(nextStep, steps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : name === 'phone' ? value.replace(/\D/g, '').slice(0, 10) : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateImageFile = (file) => {
    if (!file) return 'कृपया प्रतिमा निवडा.';
    if (!file.type.startsWith('image/')) return 'केवळ प्रतिमा फायली स्वीकारल्या जातात.';
    if (file.size > 5 * 1024 * 1024) return 'प्रतिमा 5MB पेक्षा लहान असावी.';
    return null;
  };

  const uploadPhotoFile = async (file) => {
    const validationError = validateImageFile(file);
    if (validationError) throw new Error(validationError);
    const formDataObj = new FormData();
    formDataObj.append('image', file);
    const response = await contentAPI.uploadImage(tenantSlug, formDataObj);
    return response.data?.url;
  };

  const handlePhotoFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';
    setPhotoUploading(true);
    try {
      const url = await uploadPhotoFile(file);
      if (url) setFormData(prev => ({ ...prev, photo_url: url }));
    } catch (error) {
      setErrors(prev => ({ ...prev, submit: error.message || 'Image upload failed.' }));
    } finally {
      setPhotoUploading(false);
    }
  };

  const beginPhotoUpload = () => {
    photoInputRef.current?.click();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      const invalidStep = steps.findIndex((_, index) => Object.keys(validateStep(index)).length);
      if (invalidStep >= 0) setCurrentStep(invalidStep);
      return;
    }

    try {
      setSubmitting(true);
      const method = staffId ? 'put' : 'post';
      const endpoint = staffId
        ? `/tenant/${tenantSlug}/staff/${staffId}`
        : `/tenant/${tenantSlug}/staff`;

      await api[method](endpoint, normalizeStaffPayload(formData));
      setSuccess(true);
      setTimeout(() => {
        navigate(`/admin/${tenantSlug}/staff`);
      }, 1500);
    } catch (error) {
      setErrors({ submit: apiErrorMessage(error, 'Failed to save staff member') });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="form-container staff-member-form">Loading...</div>;
  }

  const age = formData.date_of_birth ? calculateAge(formData.date_of_birth) : null;

  return (
    <div className="form-container staff-member-form">
      <h1>{staffId ? 'Edit Staff Member' : 'Add New Staff Member'}</h1>

      {success && <div className="success-message">Staff member saved successfully!</div>}
      {errors.submit && <div style={{ color: '#e74c3c', marginBottom: '20px' }}>{errors.submit}</div>}

      <form onSubmit={handleSubmit}>
        <nav className="staff-form-stepper" aria-label="Staff form steps">
          {steps.map((step, index) => (
            <button
              type="button"
              key={step.title}
              className={`${index === currentStep ? 'active' : ''} ${index < currentStep ? 'completed' : ''}`}
              disabled={index > currentStep}
              onClick={() => moveStep(index)}
              aria-current={index === currentStep ? 'step' : undefined}
            >
              <span>{index < currentStep ? '✓' : index + 1}</span>
              <div><strong>{step.title}</strong><small>{step.subtitle}</small></div>
            </button>
          ))}
        </nav>
        <div className="form-inner">
        {/* Personal Information */}
        {currentStep === 0 && <div className="form-section staff-step-panel">
          <h3>व्यक्तिगत माहिती (Personal Information)</h3>

          <TextInput
            label="Full Name (पूर्ण नाव)"
            name="full_name"
            value={formData.full_name}
            onChange={handleChange}
            error={errors.full_name}
            placeholder="Full name in English"
            required
          />

          <div className="form-row">
            <DateInput
              label="Date of Birth (जन्मतारीख)"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              error={errors.date_of_birth}
              max={new Date().toISOString().split('T')[0]}
            />

            <SelectInput
              label="Gender (लिंग)"
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              options={genderOptions}
            />
          </div>

          <div style={{ display: 'grid', gap: 8 }}>
            <label className="registry-field" htmlFor="photo_url">
              <span>Profile Photo URL (प्रोफाइल फोटो URL)</span>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  id="photo_url"
                  name="photo_url"
                  value={formData.photo_url}
                  onChange={handleChange}
                  placeholder="https://example.com/photo.jpg"
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={beginPhotoUpload}
                  disabled={photoUploading}
                >
                  {photoUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </label>
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handlePhotoFile}
            />
          </div>

          {age && (
            <div className="info-message">
              Age: {age} years
            </div>
          )}
        </div>}

        {/* Designation & Department */}
        {currentStep === 1 && <div className="form-section staff-step-panel">
          <h3>नियुक्ती तपशील (Designation Details)</h3>

          <TextInput
            label="Designation (English) (पदनाम)"
            name="designation"
            value={formData.designation}
            onChange={handleChange}
            error={errors.designation}
            placeholder="e.g., Gram Vikas Adhikari"
            required
          />

          <TextInput
            label="Designation (Marathi) (पदनाम मराठी)"
            name="designation_marathi"
            value={formData.designation_marathi}
            onChange={handleChange}
            placeholder="e.g., ग्रामविकास अधिकारी"
          />

          <SelectInput
            label="Category (वर्ग)"
            name="category"
            value={formData.category}
            onChange={handleChange}
            options={categoryOptions}
          />

          <SelectInput
            label="Department (विभाग)"
            name="department_id"
            value={formData.department_id}
            onChange={handleChange}
            options={departments}
          />
        </div>}

        {/* Contact Information */}
        {currentStep === 2 && <div className="form-section staff-step-panel">
          <h3>संपर्क माहिती (Contact Information)</h3>

          <PhoneInput
            label="Personal Phone (व्यक्तिगत फोन) *"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            error={errors.phone}
            required
          />

          <PhoneInput
            label="Office Phone (कार्यालय फोन)"
            name="office_phone"
            value={formData.office_phone}
            onChange={handleChange}
          />

          <EmailInput
            label="Email (ईमेल)"
            name="email"
            value={formData.email}
            onChange={handleChange}
            error={errors.email}
          />

          <TextArea
            label="Address (पता)"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows={2}
          />
        </div>}

        {/* Professional Information */}
        {currentStep === 3 && <div className="form-section staff-step-panel">
          <h3>व्यावसायिक माहिती (Professional Information)</h3>

          <TextInput
            label="Qualification (शिक्षा)"
            name="qualification"
            value={formData.qualification}
            onChange={handleChange}
            placeholder="e.g., Bachelor of Arts"
          />

          <NumberInput
            label="Experience (Years) (अनुभव)"
            name="experience_years"
            value={formData.experience_years}
            onChange={handleChange}
            error={errors.experience_years}
            min="0"
            max="50"
          />

          <TextInput
            label="Specialization (विशेषज्ञता)"
            name="specialization"
            value={formData.specialization}
            onChange={handleChange}
            placeholder="Special skills or expertise"
          />

          <TextArea
            label="Certifications (प्रमाणपत्र)"
            name="certifications"
            value={formData.certifications}
            onChange={handleChange}
            rows={2}
            placeholder="List certifications (one per line)"
          />
        </div>}

        {/* Employment Details */}
        {currentStep === 3 && <div className="form-section staff-step-panel">
          <h3>नियुक्ती माहिती (Employment Details)</h3>

          <DateInput
            label="Appointment Date (नियुक्ती तारीख)"
            name="appointment_date"
            value={formData.appointment_date}
            onChange={handleChange}
            error={errors.appointment_date}
            max={new Date().toISOString().split('T')[0]}
          />

          <TextInput
            label="Office Location (कार्यालय स्थान)"
            name="office_location"
            value={formData.office_location}
            onChange={handleChange}
            placeholder="e.g., Main Office, Room No. 101"
          />

          <SelectInput
            label="Availability (उपलब्धता)"
            name="availability"
            value={formData.availability}
            onChange={handleChange}
            options={availabilityOptions}
          />
        </div>}

        {/* Visibility & Status */}
        {currentStep === 4 && <div className="form-section staff-step-panel">
          <h3>दृश्यमानता व स्थिति (Visibility & Status)</h3>

          <CheckboxInput
            label="Public Visible (सार्वजनिकरित्या दृश्यमान) - Show on public portal"
            name="public_visible"
            checked={formData.public_visible}
            onChange={handleChange}
          />

          <CheckboxInput
            label="Active (सक्रिय) - Staff member is currently employed"
            name="is_active"
            checked={formData.is_active}
            onChange={handleChange}
          />
          <div className="staff-review-grid">
            <div><span>Staff member</span><strong>{formData.full_name || '—'}</strong></div>
            <div><span>Designation</span><strong>{formData.designation || '—'}</strong></div>
            <div><span>Mobile</span><strong>{formData.phone || '—'}</strong></div>
            <div><span>Department</span><strong>{departments.find(item => String(item.value) === String(formData.department_id))?.label || 'Not assigned'}</strong></div>
          </div>
        </div>}

        {/* Actions */}
        </div>
        <div className="form-actions staff-step-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate(`/admin/${tenantSlug}/staff`)}
          >
            Cancel
          </button>
          <div>
            {currentStep > 0 && <button type="button" className="btn-secondary" onClick={() => moveStep(currentStep - 1)}>← Previous</button>}
            {currentStep < steps.length - 1 ? (
              <button type="button" className="btn-primary" onClick={() => moveStep(currentStep + 1)}>Next →</button>
            ) : (
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : staffId ? 'Update Staff Member' : 'Add Staff Member'}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
